import { PencilIcon, UserCircleIcon } from '@heroicons/react/20/solid';

import useAuth from '@/hooks/useAuth';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

import { MissingTimesheetShift, MissingTimesheetWorker } from '../api/types';
import {
  formatClock,
  formatDuration,
  formatShiftDateTime,
} from '../util/dateFormat';

import Avatar from './Avatar';

// Missing-timesheet card = a shift header (Date & time · Client · Skill ·
// Location) with a drill-in chevron, and the worker rows beneath it. Because
// "missing" means no data was ever captured, the time columns render "--:--" /
// "--" in red.
//
// ## Layout: ONE grid definition, shared (GRAVY-10318)
//
// The header sized itself on the flex bases 432/220/200/140 and the worker rows
// on 363/160/four-equal-flex-columns plus a `margin-left: auto` action group.
// Nothing kept those in step, so Check-in, Check-out, Break and Total landed
// wherever the arithmetic put them — and squeezed narrow they ran into each
// other. Both now use `dashv2-tsgrid`, a single `grid-template-columns`, so the
// alignment is structural instead of two sets of numbers kept in step by hand.
//
// The worker row is finer-grained than the header, so the header cells SPAN:
//
//   Date & time  (col 1)      -> worker avatar + name
//   Client       (col 2)      -> Status + badge
//   Skill        (cols 3-4)   -> Check-in | Check-out
//   Location     (cols 5-6)   -> Break    | Total
//   (chevron)    (cols 7-8)   -> Edit hours | Call worker
//
// Every time column is its own grid track, which is what makes "Check-in and
// Check-out never merge" a property of the layout rather than of the current
// window width.
//
// It is scoped to this card rather than applied to `.dashv2-nshead`, which No
// Show also uses — with four one-to-one columns and a single action, so a
// different column count (see `.dashv2-nsgrid`).
//
// ## The chevron is the drill-in, not a toggle
//
// Worker rows are ALWAYS rendered — the approved behaviour is expanded, and
// there is no collapse. The right-facing chevron opens THIS occurrence in the
// shared Shift Detail panel, which is the flow the separate "Shift details" text
// button used to carry (GRAVY-10297): the card's own `shift_id`, over the queue,
// so the list stays mounted and closing returns to the same filters, page and
// scroll position. Two controls for one card were the compromise made when the
// chevron still collapsed the rows; with the collapse gone the text button is
// redundant, and the chevron now matches No Show and Needs Approval.
//
// ## Resolution is worker-scoped
//
// Each row therefore carries its OWN actions (GRAVY-10307): a pencil that opens
// the Enter Hours dialog for that worker on that occurrence, and a call action
// for that worker. A shift-level action could not say which of several missing
// workers it meant, and the panel's phone number belongs to the shift
// supervisor — sending Ops to the wrong person entirely.

type Props = {
  shift: MissingTimesheetShift;
  /** Opens the Shift Detail panel. Omitted only in isolated component tests. */
  onSelect?: (shiftId: number) => void;
  /** Opens the Enter Hours dialog for one worker on this shift. */
  onResolveWorker?: (worker: MissingTimesheetWorker) => void;
};

const MissingTimesheetShiftCard = ({
  shift,
  onSelect,
  onResolveWorker,
}: Props) => {
  const { currentAdmin, isCurrentCustomerAdmin } = useAuth();
  // Guarded on null — `isCurrentCustomerAdmin` reads `.customer` and throws
  // while the session resolves. Defaulting to NOT tenant-admin is the safe
  // direction: the shortcut stays hidden until the role is known.
  const tenantAdmin = !!currentAdmin && !isCurrentCustomerAdmin(currentAdmin);

  const tz = shift.timezone;

  const col = (label: string, value: string, modifier: string) => (
    <div className={`dashv2-nscol dashv2-nscol--${modifier}`}>
      <p className="dashv2-nslabel">{label}</p>
      <p className="dashv2-nsvalue" title={value}>
        {value || '—'}
      </p>
    </div>
  );

  const timeCol = (label: string, value: string, hasData: boolean) => (
    <div className="dashv2-tscol">
      <p className="dashv2-nslabel">{label}</p>
      <p className={`dashv2-tscol-val${hasData ? ' has-data' : ''}`}>{value}</p>
    </div>
  );

  return (
    <div className="dashv2-nscard">
      <div className="dashv2-nshead dashv2-tsgrid">
        {col(
          'Date & time',
          formatShiftDateTime(shift.start_at, shift.end_at, tz),
          'date',
        )}
        {col('Client', shift.client_name || '', 'client')}
        {col('Skill', shift.skill_name || '', 'skill')}
        {col('Location', shift.location_code || '', 'loc')}
        <button
          type="button"
          className="dashv2-nsdrill"
          id={`dashv2-ts-detail-${shift.shift_id}`}
          aria-label="Open shift detail"
          disabled={!onSelect}
          onClick={(e) => {
            // Focus explicitly before the panel mounts: the panel hands focus
            // back to whatever was active when it opened, and Safari does not
            // focus a button on click.
            e.currentTarget.focus();
            onSelect?.(shift.shift_id);
          }}
        >
          <ChevronRightIcon />
        </button>
      </div>

      <div className="dashv2-nsworkers">
        {shift.workers.map((w) => (
          <div className="dashv2-nsrow dashv2-tsgrid" key={w.id}>
            <div className="dashv2-nsuser">
              <Avatar name={w.name} src={w.avatar_url} />
              <span className="dashv2-nsname">
                {w.name || 'Unknown worker'}
              </span>
            </div>
            <div className="dashv2-nsfield">
              <p className="dashv2-nslabel">Status</p>
              <span className="dashv2-nsbadge">Missing timesheet</span>
            </div>
            {timeCol('Check-in', formatClock(w.check_in, tz), !!w.check_in)}
            {timeCol('Check-out', formatClock(w.check_out, tz), !!w.check_out)}
            {timeCol(
              'Break',
              formatDuration(w.break_minutes),
              w.break_minutes != null,
            )}
            {timeCol(
              'Total',
              formatDuration(w.total_minutes),
              w.total_minutes != null,
            )}
            {/* The resolution action, beside Total — the column it writes to.
                An icon rather than the old "Enter hours" text button, which was
                wide enough to force the time columns into each other. The
                accessible name is unchanged, so it still says which worker it
                means. */}
            <div className="dashv2-tsedit">
              <button
                type="button"
                className="dashv2-tsedit-btn"
                disabled={!onResolveWorker}
                aria-label={`Enter hours for ${w.name || 'worker'}`}
                onClick={() => onResolveWorker?.(w)}
              >
                <PencilIcon />
              </button>
            </div>
            {/* Only rendered when the worker actually has a number on file and
                the caller is allowed to see it — the server withholds it from
                customer-restricted admins. The CELL is always rendered so a
                worker without one cannot pull the other columns out of
                alignment. */}
            <div className="dashv2-nsaction">
              {/* Worker Profile, replacing the call action (GRAVY-10333).
                  Ops need the worker's context and history from a queue, not a
                  phone dialer.

                  TENANT ADMIN ONLY, and gated by RENDERING — not by hiding.
                  A Client Admin gets no element at all, so there is nothing to
                  reveal through keyboard focus or the DOM. Server-side profile
                  authorization still applies; this is the UI half only. */}
              {tenantAdmin && (
                <Link
                  className="dashv2-nsprofile"
                  to={`/workers_v2/${w.worker_id}`}
                  aria-label={`View worker profile for ${w.name || 'worker'}`}
                  // The chevron opens Shift Detail; the profile must never also
                  // trigger it should the row become clickable.
                  onClick={(e) => e.stopPropagation()}
                >
                  <UserCircleIcon />
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MissingTimesheetShiftCard;
